'use client';

import React, { useEffect, useRef, useState } from 'react';
import type { LandscapeGallery, Media, Video } from '@/types';
import { firstFrame, getMediaData, getFilmData, resizedImageProps } from '@/lib/utils/media.utils';

interface LandscapeGalleryProps {
  layout: LandscapeGallery;
}

interface MediaItemData {
  url: string | null;
  caption: string;
  isVideo: boolean;
  alt?: string;
  mimeType?: string | null;
  filesize?: number | null;
  filename?: string | null;
  width?: number;
  height?: number;
}

interface MediaItem {
  media: {
    video?: (number | null) | Video;
    image?: (number | null) | Media;
    caption?: string | null;
    id?: string | null;
  };
  isVideo: boolean;
}

function GalleryFrame({ aspect, media, onPortrait }: { aspect: string; media: MediaItemData; onPortrait?: (portrait: boolean) => void }) {
  const film = useRef<HTMLVideoElement>(null);
  // A film says which way up it is once its metadata has arrived, which on a server-rendered
  // page can be before this code is listening: ask at once, then listen.
  useEffect(() => {
    const el = film.current;
    if (!el || !onPortrait) return undefined;
    const report = () => {
      if (el.videoWidth && el.videoHeight) onPortrait(el.videoHeight > el.videoWidth);
    };
    report();
    el.addEventListener('loadedmetadata', report);
    return () => el.removeEventListener('loadedmetadata', report);
  }, [onPortrait, media.url]);

  if (!media.url) return null;

  return (
    <div className={`relative w-full overflow-hidden rounded-sm ${aspect}`}>
      {media.isVideo ? (
        <video
          ref={film}
          src={firstFrame(media.url)}
          className="absolute inset-0 h-full w-full object-contain"
          controls
          playsInline
          preload="metadata"
        />
      ) : (
        <img
          {...resizedImageProps(media.url, 1080, '(max-width: 768px) 100vw, 50vw')}
          alt={media.caption}
          className="absolute inset-0 h-full w-full object-contain transition-transform duration-500 "
        />
      )}
    </div>
  );
}

export const Landscape_Gallery: React.FC<LandscapeGalleryProps> = ({ layout }) => {
  const [filmPortrait, setFilmPortrait] = useState(false);
  const allMedia = layout.media?.map(v => ({ media: v, isVideo: true }));

  // İlk iki medyayı ve diğerlerini ayır
  const firstMedia = allMedia?.[0];
  const secondMedia = allMedia?.[1];
  const otherMedia = allMedia?.slice(2);

  // Medya URL'lerini ve caption bilgisini al (öncelik video olacak şekilde)
  const getMediaUrl = (mediaItem: MediaItem): MediaItemData => {
    const caption = mediaItem?.media?.caption || '';

    if (mediaItem?.media?.video) {
      return {
        ...getFilmData(mediaItem.media.video),
        caption,
        isVideo: true,
      };
    } else if (mediaItem?.media?.image) {
      return {
        ...getMediaData(mediaItem.media.image),
        caption,
        isVideo: false,
      };
    }
    return { url: null, caption: '', isVideo: false };
  };

  const firstMediaData = firstMedia ? getMediaUrl(firstMedia) : { url: null, caption: '', isVideo: false };
  const secondMediaData = secondMedia ? getMediaUrl(secondMedia) : { url: null, caption: '', isVideo: false };
  const otherMediaData = otherMedia?.map(media => getMediaUrl(media)) || [];

  // A vertical film or photograph leads the gallery standing up, two rows tall, with the next
  // item wide beside it, instead of being cropped to a wide strip. A photograph's shape is in
  // its record; a film reports its own.
  const featuredPortrait = firstMediaData.isVideo
    ? filmPortrait
    : ((firstMediaData as MediaItemData).height ?? 0) > ((firstMediaData as MediaItemData).width ?? 0);

  return (
    <section className="w-full">
      <div className="p-[15px] rounded-md bg-white mb-[12px] flex flex-col justify-between relative pt-4">
        <div className="content-section bg-black">
          <div className="flex justify-between flex-row items-start">
            <div className="text-left mb-2">
              <div className="uppercase text-gray-500 text-[10px]">[ {layout.leftTitle} ]</div>
            </div>
            <p className="text-zinc-400 text-[10px] font-mono">{layout.rightTitle}</p>
          </div>

          {/* Gallery Grid with featured layout */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 pt-4">
            <div className={`group relative min-w-0 ${featuredPortrait ? 'md:col-span-4 md:row-span-2' : 'md:col-span-8'}`}>
              <GalleryFrame aspect={featuredPortrait ? 'aspect-[9/16]' : 'aspect-[2/1]'} media={firstMediaData} onPortrait={setFilmPortrait} />
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-white text-[10px] font-mono">{firstMediaData.caption || ''}</p>
              </div>
            </div>

            {/* Right column - spans 4 columns */}
            <div className={`group relative min-w-0 ${featuredPortrait ? 'md:col-span-8' : 'md:col-span-4'}`}>
              <GalleryFrame aspect={featuredPortrait ? 'aspect-[2/1]' : 'aspect-square'} media={secondMediaData} />
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-white text-[10px] font-mono">{secondMediaData.caption || ''}</p>
              </div>
            </div>

            {otherMediaData.map((media, index) => (
              <div key={`other-media-${index}`} className="md:col-span-4 group relative min-w-0">
                <GalleryFrame aspect="aspect-[4/3]" media={media} />
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-white text-[10px] font-mono">{media.caption || ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
